import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tripsApi } from '../../api/trips.api';
import { LANGUAGES } from '../../i18n';
import { toast } from '../ui/Toast';
import { Languages, Icon } from '../ui/Icon';
import type { Trip } from '../../types';

/**
 * Trip-level language for everything the AI writes into the trip. Deliberately
 * not the reader's UI language: the content is shared, so it can't follow
 * whoever happens to be chatting — that's what made a single trip fill up with
 * proposals in several languages. Each user's chat replies still come back in
 * their own language.
 *
 * Lives in the trip's manage modal, next to the participants: it is a setting
 * of the trip, not a piece of its content (it used to sit under the tips, which
 * read as if it were one more tip).
 */
export default function TripContentLanguage({ trip, canEdit, onSaved }: {
  trip: Trip;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);

  async function handleChange(next: string) {
    if (next === trip.contentLanguage) return;
    setSaving(true);
    try {
      await tripsApi.update(trip.id, { contentLanguage: next });
      toast(t('trip.contentLanguage.saved'), 'success');
      onSaved();
    } catch {
      toast(t('trip.contentLanguage.failed'), 'error');
    } finally {
      setSaving(false);
    }
  }

  const current = LANGUAGES.find(l => l.code === trip.contentLanguage);

  return (
    <section className="mt-6 pt-5 border-t border-slate-200/70 dark:border-slate-700/60 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-start gap-2.5 min-w-0">
        <Icon as={Languages} className="mt-0.5 shrink-0 text-slate-400 dark:text-slate-500" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {t('trip.contentLanguage.title')}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {t('trip.contentLanguage.hint')}
          </p>
        </div>
      </div>

      {canEdit ? (
        <select
          className="input py-1 text-xs w-auto"
          value={trip.contentLanguage}
          disabled={saving}
          aria-label={t('trip.contentLanguage.title')}
          onChange={e => handleChange(e.target.value)}
        >
          {LANGUAGES.map(l => (
            <option key={l.code} value={l.code}>{l.name}</option>
          ))}
        </select>
      ) : (
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {current?.name ?? trip.contentLanguage}
        </span>
      )}
    </section>
  );
}
